import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Button, Checkbox, FormControlLabel, TextField } from '@mui/material';
import { Notifications, Orders } from '../../store/slices';
import { setCancelOrderItem, setDeclineOrderItem } from '../../api/orders';
import { AppDialog } from '../ui';
import { GENERIC_ERROR_MESSAGE } from '../../lib/errorMessage';

/** Cancel or decline one item of the order (extra-features clients). */
interface OrderItemCancelFormProps {
    isPopUp: boolean;
    popUpHandler: (open: boolean) => void;
    orderItemId: number | string | null;
    actionType: 'cancel' | 'decline' | null;
}

export default function OrderItemCancelForm({ isPopUp, popUpHandler, orderItemId, actionType }: OrderItemCancelFormProps) {
    const [cancelMessage, setCancelMessage] = useState('');
    const [requireCancelPayment, setRequireCancelPayment] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pending, setPending] = useState(false);
    const dispatch = useDispatch();
    const { id, companyId } = useParams();
    const verb = actionType === 'cancel' ? 'Cancel' : 'Decline';

    useEffect(() => {
        if (isPopUp) {
            setCancelMessage('');
            setRequireCancelPayment(actionType === 'cancel');
            setError(null);
        }
    }, [isPopUp, actionType]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setCancelMessage(event.target.value);
        setError(event.target.value.length < 1 ? 'A reason is required' : null);
    };

    const submitAction = async () => {
        if (pending) return;
        setPending(true);
        try {
            const request = actionType === 'cancel' ? setCancelOrderItem : setDeclineOrderItem;
            await request({
                companyId,
                orderId: id,
                orderItemId,
                cancelMessage,
                requireCancelPayment,
                onSuccess: () => {
                    dispatch(Notifications.setNotifications({ text: actionType === 'cancel' ? 'Order item canceled' : 'Order item declined', type: 'success' }));
                    popUpHandler(false);
                    dispatch<any>(Orders.getOrder({ id: id || '', companyId: companyId || '' }));
                },
            });
        } catch (error) {
            console.warn(`[orders] item ${actionType} failed`, error);
            dispatch(Notifications.setNotifications({ text: GENERIC_ERROR_MESSAGE, type: 'error' }));
        } finally {
            setPending(false);
        }
    };

    const submit = () => {
        if (cancelMessage.length < 1) {
            setError('A reason is required');
            return;
        }
        setError(null);
        submitAction();
    };

    return (
        <AppDialog
            open={Boolean(isPopUp)}
            onClose={() => popUpHandler(false)}
            title={`${verb} item ${orderItemId ?? ''}`}
            description="Only this item changes; the rest of the order continues. The reason is stored in the order history."
            pending={pending}
            maxWidth="xs"
            actions={(
                <>
                    <Button variant="outlined" onClick={() => popUpHandler(false)}>Close</Button>
                    <Button variant="contained" color="error" onClick={submit} disabled={pending}>{verb} item</Button>
                </>
            )}
        >
            <TextField
                label={`Reason for ${actionType === 'cancel' ? 'canceling' : 'declining'}`}
                name="reason"
                multiline
                rows={4}
                fullWidth
                value={cancelMessage}
                onChange={handleInputChange}
                error={Boolean(error)}
                helperText={error}
                required
                autoFocus
            />
            <FormControlLabel
                sx={{ mt: 1 }}
                control={<Checkbox checked={requireCancelPayment} onChange={(event) => setRequireCancelPayment(event.target.checked)} />}
                label="Require cancel payment"
            />
        </AppDialog>
    );
}
